/**
 * PLT-4839: react-query data hooks for the chat-history sidebar.
 *
 * Talks to the four endpoints registered in `services/main/upserve/modules/chat/conversations.ts`:
 *
 *   GET    /chat/conversations
 *   GET    /chat/conversations/:id
 *   PATCH  /chat/conversations/:id
 *   DELETE /chat/conversations/:id
 *
 * Conventions follow the rest of the repo:
 *   - `fetchOrThrow` for reads (plays well with TanStack Query's error
 *     surface).
 *   - `useMutation` for writes, with `onSuccess` invalidation of the list
 *     query so a delete / rename reflects in the sidebar immediately.
 *   - One single `queryKey` family (`['chat', 'conversations']`) so
 *     mutations don't have to enumerate every consumer.
 */
import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { fetchOrThrow, fetchOrThrowNoReturnData } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { ErrorWithHttpStatus } from '@upright/ui/api/common/error-with-http-status';

export interface ChatConversationListItem {
  id: string;
  title: string | null;
  titleIsAutogen: boolean;
  pageContextHref: string | null;
  createdAt: string;
  updatedAt: string;
  firstUserMessageSnippet: string | null;
  /**
   * PLT-7658: on a search only, the message the chat matched in, as plain text starting just before
   * the first match. Null when only the title matched; absent outside a search.
   */
  matchSnippet?: string | null;
}

/** One page of `GET /chat/conversations`, as the Chats page pages through it (PLT-7658). */
export interface ChatConversationPage {
  items: ChatConversationListItem[];
  hasMore: boolean;
  /** How many conversations the list or the search has in all, across every page. */
  total: number;
}

export interface ChatConversationDetailMessage {
  messageIndex: number;
  role: 'user' | 'assistant';
  content: string;
  contentEvents: unknown[] | null;
  createdAt: string;
  /**
   * PLT-5741: the user's persisted feedback on this assistant turn, so the
   * vote control rehydrates its filled/empty state on reload. Null on user
   * rows and unrated assistant rows.
   */
  feedback: {
    rating: 'positive' | 'negative';
    reasonChips: string[];
    reasonText: string | null;
  } | null;
}

export interface ChatConversationDetail extends ChatConversationListItem {
  messages: ChatConversationDetailMessage[];
}

/**
 * The label a surface shows for a conversation: its manual or auto-generated title, falling back
 * to the snippet of the first user message, falling back to a generic placeholder.
 *
 * Shared rather than repeated (PLT-6529). Two surfaces now list the same conversations — the
 * `/chat` history sidebar and the unified investor rail's Chats group — and a row the sidebar
 * calls "Portfolio emissions" while the rail calls it "New conversation" reads as two different
 * conversations. They already read the same list query; one title helper on top of it is what
 * makes them agree by construction rather than by review.
 *
 * The last fallback is not defensive padding: both earlier fields are legitimately absent on a
 * brand-new conversation, because the optimistic row is inserted the moment the user sends and
 * the title summariser has not run yet. `sanitizeListItem` normalises an empty snippet back to
 * null so the `??` chain reaches this placeholder instead of rendering a blank row.
 */
export function conversationDisplayTitle(item: ChatConversationListItem): string {
  return item.title ?? item.firstUserMessageSnippet ?? 'New conversation';
}

const LIST_QUERY_KEY = ['chat', 'conversations'] as const;

/**
 * The Chats page's own pages, one entry per search (`''` for the whole list). Under
 * `LIST_QUERY_KEY`, so every invalidation of the list refetches them too; kept apart from it
 * because the optimistic writes below patch the list's flat array in place, and these entries hold
 * pages rather than a flat array.
 */
const PAGES_QUERY_KEY = ['chat', 'conversations', 'pages'] as const;

/** Conversations per page on the Chats page: the server's default, and the sidebar's whole list. */
export const CHAT_CONVERSATION_PAGE_SIZE = 50;

function detailQueryKey(id: string): readonly [string, string, string] {
  return ['chat', 'conversations', id] as const;
}

/**
 * Module-level store of optimistic conversation rows the server hasn't
 * confirmed yet (PLT-5287).
 *
 * A brand-new conversation must show up in the sidebar the instant the
 * user sends their first prompt (Claude / ChatGPT behaviour). The row is
 * surfaced optimistically on turn-start (`upsertOptimisticConversation`),
 * but the canonical row is written by the *fire-and-forget* `persistChatTurn`,
 * which only commits a few hundred ms after the SSE stream ends. So any
 * `GET /chat/conversations` that resolves in that window returns a list
 * *without* the new conversation. Two such refetches would otherwise wipe
 * the optimistic row right back out of the cache:
 *
 *   1. the initial list fetch started on landing, if it resolves *after*
 *      the user has already sent (`setQueryData` does not cancel an
 *      in-flight fetch);
 *   2. the eager post-turn list invalidation, which fires before
 *      `persistChatTurn` commits.
 *
 * Keeping pending rows here and merging them into every list result
 * (`mergePendingOptimisticConversations`) makes the optimistic row immune
 * to both races without depending on persistence timing: it stays visible
 * until a server response actually includes it, at which point it is
 * pruned in favour of the canonical row.
 */
const pendingOptimisticConversations = new Map<string, ChatConversationListItem>();

/**
 * Merge the still-pending optimistic rows into a server-returned list.
 *
 * Prunes (as a side effect) any pending row the server now returns — the
 * server row is authoritative, so once it appears we stop synthesising our
 * own. Remaining pending rows are prepended newest-first so a brand-new
 * conversation sits at the top of the sidebar while it persists. Called
 * from the list query's `queryFn`, so every refetch reconciles the two.
 */
function mergePendingOptimisticConversations(serverItems: ChatConversationListItem[]): ChatConversationListItem[] {
  if (pendingOptimisticConversations.size === 0) return serverItems;
  const serverIds = new Set(serverItems.map(item => item.id));
  for (const id of [...pendingOptimisticConversations.keys()]) {
    if (serverIds.has(id)) pendingOptimisticConversations.delete(id);
  }
  if (pendingOptimisticConversations.size === 0) return serverItems;
  const pending = [...pendingOptimisticConversations.values()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return [...pending, ...serverItems];
}

/**
 * Drop a conversation from the pending-optimistic store (PLT-5287). Called
 * when a row is deleted or renamed so a brand-new conversation that hasn't
 * persisted yet can't be re-synthesised back into the sidebar by
 * `mergePendingOptimisticConversations` after the user has acted on it.
 */
function forgetPendingOptimisticConversation(id: string): void {
  pendingOptimisticConversations.delete(id);
}

/**
 * List the current user's conversations, newest first. Excludes soft-
 * deleted rows. Used by the sidebar list — refetched on `New chat` /
 * delete / rename via `queryClient.invalidateQueries`.
 *
 * Returns `[]` while loading so the sidebar can render a "no conversations
 * yet" empty state once the query resolves and finds nothing — the list
 * row component itself decides whether the empty state is a true empty
 * (no history) or a still-loading state, by looking at `isPending`.
 */
/**
 * PLT-4865: defensive client-side strip of the deep-link `[Context: …]`
 * prefix from `firstUserMessageSnippet`. The server's list + detail
 * endpoints already strip on read (see
 * `services/main/upserve/modules/chat/hidden-context-prefix.ts`), so on
 * production this is a no-op. The reason we still mirror it
 * client-side: the UI can be deployed ahead of the backend (e.g. via
 * `yarn deploy-ui-preview` against the production backend, or any
 * window between a frontend deploy and a matching backend deploy).
 * In that staging gap, the API still returns the unstripped snippet
 * for legacy rows, and we don't want a "[Context: entityId=cac88d…]"
 * row glitching back into the sidebar. Idempotent on rows that don't
 * carry the prefix.
 */
function stripHiddenContextPrefix(text: string | null): string | null {
  if (text === null) return null;
  // Mirrors the server-side helper in `chat/hidden-context-prefix.ts`,
  // including the truncated-prefix branch — see that file for the
  // rationale. The truncated branch matters for the legacy-row
  // backward-compat path: the prior server truncated the snippet at
  // 80 chars BEFORE PLT-4865 added the strip, so a row's snippet may
  // start with `[Context:` and be missing the closing `]`. We treat
  // that as "the user prompt is already lost" and return `''` rather
  // than leaking a half-bracketed protocol line into the sidebar.
  const fullMatch = /^\[Context:[^\]]*\](?:\r?\n){1,2}/.exec(text);
  if (fullMatch) {
    return text.slice(fullMatch[0].length).replace(/^\s+/, '');
  }
  if (/^\[Context:/.test(text)) {
    return '';
  }
  return text;
}

function sanitizeListItem(item: ChatConversationListItem): ChatConversationListItem {
  // After stripping a truncated `[Context: …` prefix that had no
  // closing `]`, the remainder is the empty string — there's nothing
  // visible the user typed, because the prior server truncated the
  // row at 80 chars BEFORE the strip existed. Convert empty back to
  // null so `conversationDisplayTitle`'s `?? 'New conversation'`
  // fallback fires (??-coalescing only treats null/undefined as
  // missing, not empty string).
  const stripped = stripHiddenContextPrefix(item.firstUserMessageSnippet);
  return { ...item, firstUserMessageSnippet: stripped === '' ? null : stripped };
}

export function useChatConversationList() {
  return useQuery({
    queryKey: LIST_QUERY_KEY,
    queryFn: async () => {
      const data = await fetchOrThrow<{ items: ChatConversationListItem[] }>('/chat/conversations');
      // Merge in any optimistic rows the server hasn't persisted yet so a
      // brand-new conversation isn't wiped out by a refetch that races the
      // fire-and-forget `persistChatTurn` write (PLT-5287). The pending row
      // is read *after* the await, so even an in-flight fetch that started
      // before the user sent picks it up on resolve.
      return mergePendingOptimisticConversations(data.items.map(sanitizeListItem));
    },
    // Conversations don't change without our own mutations, so stale-while-
    // revalidate behaviour is correct. We rely on explicit invalidation
    // when a new turn finishes (the chat handler writes a row, but the
    // browser holds the list cache) — see `refreshConversationListUntilPersisted`.
    staleTime: 30 * 1000,
  });
}

/**
 * The full list of the Chats page and the chat side panel's Chats view, a page at a time, or the
 * conversations matching `query` when it is not empty (PLT-7658). The sidebar and the rail keep
 * reading `useChatConversationList`, which is one page and carries the optimistic rows. This one
 * has no optimistic rows: the Chats page is never open while a turn is running, and the side panel
 * refetches it each time its Chats view opens (`expireChatConversationLists`), so the one
 * conversation it can leave out is a first turn still streaming at that moment.
 */
export function useChatConversationPagesQuery(query: string) {
  return useInfiniteQuery({
    queryKey: [...PAGES_QUERY_KEY, query],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(CHAT_CONVERSATION_PAGE_SIZE), offset: String(pageParam) });
      if (query !== '') {
        params.set('q', query);
      }
      const page = await fetchOrThrow<ChatConversationPage>(`/chat/conversations?${params.toString()}`);
      return { ...page, items: page.items.map(sanitizeListItem) };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.hasMore ? lastPageParam + CHAT_CONVERSATION_PAGE_SIZE : undefined,
    staleTime: 30 * 1000,
  });
}

/**
 * Mark the cached conversation lists stale, so the next list to mount refetches them (PLT-7883):
 * every page of the full list, and the flat list, which `ChatsSearch` reads to tell whether there
 * are any chats to search. The chat side panel calls it as its Chats view opens: that view opens
 * over any page, often right after a turn in the panel, and on most pages nothing that keeps the
 * lists in step with the chat is mounted (`useChatConversationListSync` runs in the `/chat` sidebar
 * and the investor rail only), so lists cached less than the `staleTime` ago would show the chats
 * as they were before that turn. Stale rather than refetched here, so a list mounting on an empty
 * cache fetches once. The flat list's key is matched exactly: the pages and every conversation's
 * detail sit under it.
 */
export function expireChatConversationLists(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: PAGES_QUERY_KEY, refetchType: 'none' });
  void queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY, exact: true, refetchType: 'none' });
}

/** A Chats page entry with conversation `id` taken out of every loaded page, and out of the count. */
function withoutConversation(
  data: InfiniteData<ChatConversationPage, number> | undefined,
  id: string,
): InfiniteData<ChatConversationPage, number> | undefined {
  if (data === undefined || !data.pages.some(page => page.items.some(item => item.id === id))) {
    return data;
  }
  return {
    ...data,
    pages: data.pages.map(page => ({
      ...page,
      items: page.items.filter(item => item.id !== id),
      total: page.total - 1,
    })),
  };
}

/**
 * Fetch one conversation with its full message list as a React Query
 * cache entry. The query cache stays warm across row-clicks so a
 * second click on the same row re-uses the previous result; the
 * matching imperative variant `fetchChatConversationDetail` (below) is
 * used by the chat-history list's row-click handler (PLT-4883) to
 * await the same cache entry from inside an event handler.
 */
export function useChatConversation(id: string | null) {
  return useQuery({
    queryKey: id ? detailQueryKey(id) : ['chat', 'conversations', '<none>'],
    queryFn: async () => await fetchConversationDetailFromApi(id),
    enabled: id !== null,
  });
}

/**
 * Imperative version of `useChatConversation` — fetches the detail
 * row through the same `queryKey` family so the cache is shared with
 * any subscribed `useChatConversation(id)` consumers (a follow-up
 * read after rehydration is a free cache hit).
 *
 * Used by the chat-history sidebar's row-click handler (PLT-4883) to
 * seed the assistant-ui runtime with past messages — see
 * `chat-history-list.tsx`'s `rehydrateConversation`.
 */
export async function fetchChatConversationDetail(
  queryClient: QueryClient,
  id: string,
): Promise<ChatConversationDetail> {
  return await queryClient.fetchQuery({
    queryKey: detailQueryKey(id),
    // PLT-5137: retry transient failures inside the queryFn rather than
    // relying on react-query's `retry` option. `fetchQuery` is called
    // imperatively from the row-click handler, so a thrown error here is
    // what the handler catches; retrying in-band means a successful detail
    // still lands in the cache (and a later `useChatConversation(id)` gets a
    // free hit), while a transient blip no longer surfaces as a click that
    // "did nothing". See `fetchConversationDetailWithRetry`.
    queryFn: async () => await fetchConversationDetailWithRetry(id),
  });
}

async function fetchConversationDetailFromApi(id: string | null): Promise<ChatConversationDetail> {
  if (!id) throw new Error('fetchConversationDetailFromApi called without an id');
  return await fetchOrThrow<ChatConversationDetail>(`/chat/conversations/${encodeURIComponent(id)}`);
}

/**
 * PLT-5137: the conversation-detail GET fails sporadically for two reasons,
 * and both used to leave the user silently on the chat landing page (the
 * row-click handler swallowed the error):
 *
 *   1. Optimistic-insert race on a brand-new conversation. The sidebar
 *      surfaces the row the instant a turn starts
 *      (`upsertOptimisticConversation`), but the server only writes the row
 *      via `persistChatTurn`, which is fire-and-forget and runs *after* the
 *      SSE stream ends. A click on the fresh row before that write commits
 *      hits `handleGetConversation` with no row → 410 `resourceGone`. The
 *      write lands a few hundred ms later, so a short retry resolves it.
 *   2. Plain transient failures (5xx during a deploy, a network blip, a
 *      timeout on a large conversation) on an established conversation.
 *
 * We retry a small, bounded number of times with linear backoff. 410 is the
 * primary "persistence still in flight" signal, so it is explicitly
 * retryable; 5xx / network errors (no `status`) are retried too. Client
 * errors that aren't 410 (e.g. a 400 on a malformed id) are not retryable —
 * retrying them would just burn time before the same failure. The caller
 * (`rehydrateConversation`) surfaces a toast if every attempt fails.
 */
const DETAIL_FETCH_MAX_ATTEMPTS = 4;
const DETAIL_FETCH_RETRY_BASE_MS = 250;

function isRetryableDetailFetchError(err: unknown): boolean {
  if (err instanceof ErrorWithHttpStatus) {
    // 410: the conversation row isn't persisted yet (optimistic-insert
    // race) — the canonical "try again in a moment" case. 5xx: a transient
    // server-side failure. Other 4xx are deterministic and not retried.
    return err.status === 410 || err.status >= 500;
  }
  // No HTTP status attached ⇒ a network-level failure (fetch rejected,
  // timeout). Treat as transient and retry.
  return true;
}

async function fetchConversationDetailWithRetry(id: string): Promise<ChatConversationDetail> {
  for (let attempt = 0; attempt < DETAIL_FETCH_MAX_ATTEMPTS; attempt++) {
    try {
      return await fetchConversationDetailFromApi(id);
    } catch (err) {
      const isLastAttempt = attempt === DETAIL_FETCH_MAX_ATTEMPTS - 1;
      // On the final attempt, or for a non-transient error, rethrow so the
      // caller (`rehydrateConversation`) can surface a toast. Rethrowing the
      // caught binding directly preserves the original error (and stack).
      if (isLastAttempt || !isRetryableDetailFetchError(err)) {
        throw err;
      }
      // Linear backoff (250ms, 500ms, 750ms) — enough to clear the
      // post-stream persistence window without making a re-click feel laggy.
      await delay(DETAIL_FETCH_RETRY_BASE_MS * (attempt + 1));
    }
  }
  // Unreachable: the final iteration always either returns or throws. The
  // throw keeps TypeScript's control-flow analysis satisfied (it can't prove
  // the loop body runs) without leaking a non-Error throw past the linter.
  throw new Error('fetchConversationDetailWithRetry exhausted attempts without resolving');
}

async function delay(ms: number): Promise<void> {
  await new Promise<void>(resolve => setTimeout(resolve, ms));
}

/**
 * Soft-delete a conversation. The list query is invalidated on success so
 * the row disappears from the sidebar. The component layer (the
 * confirmation modal) handles the optimistic UX so the row vanishes the
 * instant the user confirms — without optimism the sidebar would feel
 * sluggish on a slow connection.
 */
export function useDeleteChatConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await fetchOrThrowNoReturnData(`/chat/conversations/${encodeURIComponent(id)}`, 'DELETE');
    },
    onMutate: async id => {
      // Drop any optimistic row for this id so the list merge can't
      // re-synthesise the just-deleted conversation back into the sidebar
      // on a later refetch (PLT-5287).
      forgetPendingOptimisticConversation(id);
      // Optimistic update: remove the row from the cached list so the
      // user sees the delete take effect immediately. The server call
      // continues in the background; if it errors, `onError` restores
      // the previous list.
      await queryClient.cancelQueries({ queryKey: LIST_QUERY_KEY });
      const previous = queryClient.getQueryData<ChatConversationListItem[]>(LIST_QUERY_KEY);
      queryClient.setQueryData<ChatConversationListItem[]>(LIST_QUERY_KEY, old =>
        (old ?? []).filter(item => item.id !== id),
      );
      // The Chats page's pages too (PLT-7658), so the row also leaves the page it was deleted from
      // straight away rather than when the refetch below lands.
      const previousPages = queryClient.getQueriesData<InfiniteData<ChatConversationPage, number>>({
        queryKey: PAGES_QUERY_KEY,
      });
      queryClient.setQueriesData<InfiniteData<ChatConversationPage, number>>({ queryKey: PAGES_QUERY_KEY }, old =>
        withoutConversation(old, id),
      );
      return { previous, previousPages };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(LIST_QUERY_KEY, context.previous);
      }
      for (const [queryKey, data] of context?.previousPages ?? []) {
        queryClient.setQueryData(queryKey, data);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
    },
  });
}

export function useDeleteAllChatConversations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await fetchOrThrowNoReturnData('/chat/conversations', 'DELETE');
    },
    onSuccess: async () => {
      await queryClient.cancelQueries({ queryKey: LIST_QUERY_KEY });
      pendingOptimisticConversations.clear();
      queryClient.removeQueries({ queryKey: LIST_QUERY_KEY, predicate: query => query.queryKey.length > 2 });
      queryClient.setQueryData<ChatConversationListItem[]>(LIST_QUERY_KEY, []);
      void queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
    },
  });
}

/**
 * Rename a conversation (or update `pageContextHref`). Sends a PATCH with
 * only the fields the caller changed.
 */
export function useRenameChatConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      return await fetchOrThrow<{ id: string; title: string; titleIsAutogen: boolean }>(
        `/chat/conversations/${encodeURIComponent(id)}`,
        'PATCH',
        { title },
      );
    },
    onSuccess: data => {
      // A successful rename means the row is persisted server-side, so drop
      // any optimistic entry to stop the list merge re-adding a stale,
      // null-title pending row on the next refetch (PLT-5287).
      forgetPendingOptimisticConversation(data.id);
      // Patch the cached list in place so the sidebar updates without a
      // full refetch. We still invalidate the detail query for the
      // specific id in case the user has it open.
      queryClient.setQueryData<ChatConversationListItem[]>(LIST_QUERY_KEY, old =>
        (old ?? []).map(item => (item.id === data.id ? { ...item, title: data.title, titleIsAutogen: false } : item)),
      );
      void queryClient.invalidateQueries({ queryKey: detailQueryKey(data.id) });
    },
  });
}

const LIST_REFRESH_MAX_ATTEMPTS = 5;
const LIST_REFRESH_RETRY_BASE_MS = 250;

/**
 * Refresh the sidebar list after a turn finishes, retrying until the server
 * actually returns the just-finished conversation (PLT-5287).
 *
 * `persistChatTurn` is fire-and-forget and commits the row a few hundred ms
 * after the stream ends, so a single eager invalidation refetches too early
 * and gets a list without the new row. We invalidate (which awaits the
 * refetch) and, while the conversation is still pending — i.e.
 * `mergePendingOptimisticConversations` hasn't yet seen it in a server
 * response and pruned it from the store — back off and try again. The
 * optimistic row stays visible across every attempt via the merge, so it
 * never flickers out; once the server confirms it, the canonical row (real
 * title) takes over. Bounded so a write that never commits (persist failed)
 * can't loop forever — the optimistic row simply stays until the next
 * natural refetch.
 *
 * A conversation that was already persisted (e.g. a follow-up turn on an
 * existing conversation) was never added to the pending store, so the
 * `has` check is already false and this resolves after the first
 * invalidation — matching the previous single-invalidation behaviour.
 */
export async function refreshConversationListUntilPersisted(
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
): Promise<void> {
  for (let attempt = 0; attempt < LIST_REFRESH_MAX_ATTEMPTS; attempt++) {
    await queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
    if (!pendingOptimisticConversations.has(conversationId)) return;
    await delay(LIST_REFRESH_RETRY_BASE_MS * (attempt + 1));
  }
}

/**
 * How long to wait before looking once more for a generated title.
 *
 * The server writes it a second or so after the turn's rows commit — a Haiku
 * round-trip behind `persistChatTurn`, which itself lands a few hundred ms after
 * the stream ends (PLT-4866). Two and a half seconds is comfortably past that
 * without being long enough for the user to have navigated away and back.
 */
const TITLE_REFRESH_DELAY_MS = 2_500;

/**
 * Pick up a generated title for a conversation that has just been persisted
 * without one (PLT-4866).
 *
 * The title is written after the response stream has already closed, so there is
 * no open channel to push it down: by the time it exists, the request that
 * produced it is over. One extra delayed refetch is what makes it appear in the
 * session that produced it rather than on the next page load.
 *
 * Deliberately **one** attempt, not a poll loop like the function above. Title
 * generation declines outright on a vague opening exchange, so for some first
 * turns there will never be a title to fetch — and a loop would then spend its
 * whole budget on `GET /chat/conversations` requests for nothing. A title that misses this window is not lost; it shows on the next
 * natural refetch, which the 30-second `staleTime` makes soon.
 *
 * Both the `/chat` sidebar and the unified investor rail read `LIST_QUERY_KEY`,
 * so one invalidation updates whichever of them is mounted.
 */
export async function refreshConversationListForGeneratedTitle(
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
): Promise<void> {
  // Checked before the wait as well as after it, so a conversation that already
  // has a title — every follow-up turn, and every renamed conversation — costs
  // nothing at all.
  if (!isAwaitingGeneratedTitle(queryClient, conversationId)) return;
  await delay(TITLE_REFRESH_DELAY_MS);
  if (!isAwaitingGeneratedTitle(queryClient, conversationId)) return;
  await queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
}

/**
 * Whether the cached row for `conversationId` is one the server might still be
 * writing a title for: it has no title, and no one has renamed it (a rename
 * clears `titleIsAutogen`, server-side and in the cache patch above, which is
 * what stops us refetching over a title the user just typed).
 */
function isAwaitingGeneratedTitle(queryClient: ReturnType<typeof useQueryClient>, conversationId: string): boolean {
  const cached = queryClient.getQueryData<ChatConversationListItem[]>(LIST_QUERY_KEY);
  const row = cached?.find(item => item.id === conversationId);
  return row !== undefined && row.title === null && row.titleIsAutogen;
}

/**
 * Optimistically insert (or float-to-top) a conversation in the
 * cached list. Used by the sidebar to surface a brand-new
 * conversation row the instant the user sends their first message
 * (PLT-4861 #4) — without this, the row only appears after the turn
 * finishes and the list query is invalidated, which gives the user
 * the impression that the conversation hasn't been saved yet.
 *
 * The patch is purely client-side; the server-side `persistChatTurn`
 * still owns the canonical row. Once the turn finishes and the list
 * is invalidated, the optimistic row is replaced by the server row
 * (same `id`), with whatever fields the server actually populated
 * (title, updatedAt, …).
 */
export function upsertOptimisticConversation(
  queryClient: ReturnType<typeof useQueryClient>,
  optimistic: ChatConversationListItem,
): void {
  // Record the row so it survives any list refetch that resolves before
  // `persistChatTurn` commits the canonical row — see the
  // `pendingOptimisticConversations` rationale (PLT-5287). The
  // `setQueryData` below still runs so the row appears synchronously this
  // tick; the store is what keeps it there across refetches.
  pendingOptimisticConversations.set(optimistic.id, optimistic);
  queryClient.setQueryData<ChatConversationListItem[]>(LIST_QUERY_KEY, old => {
    const previous = old ?? [];
    // If the row already exists (e.g. the user is sending a follow-up
    // turn), bump its `updatedAt` and snippet so it floats to the top
    // and shows the right preview, but otherwise leave the existing
    // row alone — the server-stored title etc. are more authoritative
    // than anything we can synthesise here.
    const idx = previous.findIndex(item => item.id === optimistic.id);
    if (idx >= 0) {
      const next = previous.slice();
      next[idx] = {
        ...previous[idx],
        ...optimistic,
        // Don't clobber a server-provided real title with our `null`.
        title: previous[idx].title ?? optimistic.title,
        titleIsAutogen: previous[idx].title === null ? optimistic.titleIsAutogen : previous[idx].titleIsAutogen,
      };
      // Float to the top so the most recently active conversation is
      // visible without scrolling.
      const [moved] = next.splice(idx, 1);
      next.unshift(moved);
      return next;
    }
    return [optimistic, ...previous];
  });
}
